import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import { Control, useController } from "react-hook-form";
import { isEmail } from "validator";

import { parseNotificationEmails } from "../helpers";
import type { StepAdvancedSettingsFormData } from "../types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAutocompleteV2 } from "@/dashboard-design-system/DashboardAutocompleteV2/DashboardAutocompleteV2";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";

const SEPARATORS_REGEX = /[\s,;]/;
const SPLIT_REGEX = /[\s,;]+/;

interface NotificationEmailsInputProps {
  control: Control<StepAdvancedSettingsFormData>;
  label: string;
}

export const NotificationEmailsInput: FC<NotificationEmailsInputProps> = ({ control, label }) => {
  const { t } = useTranslate();
  const { logUserInputEvent } = useLiveFormAmplitudeEvents();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [hasError, setHasError] = useState(false);

  const { field } = useController({ name: "notificationEmails", control });
  const emails = useMemo(() => parseNotificationEmails(field.value) ?? [], [field.value]);

  const setEmails = (next: string[]) => {
    field.onChange(next.join(", "));
  };

  const splitEmails = (raw: string, existing: string[]): { nextEmails: string[]; leftover: string } => {
    const nextEmails = [...existing];
    const lowerCased = new Set(existing.map((email) => email.toLowerCase()));
    const invalidTokens: string[] = [];

    for (const token of raw
      .split(SPLIT_REGEX)
      .map((entry) => entry.trim())
      .filter(Boolean)) {
      if (!isEmail(token)) {
        invalidTokens.push(token);
      } else if (!lowerCased.has(token.toLowerCase())) {
        lowerCased.add(token.toLowerCase());
        nextEmails.push(token);
      }
    }

    return { nextEmails, leftover: invalidTokens.join(" ") };
  };

  const commitEmails = (raw: string): string => {
    const { nextEmails, leftover } = splitEmails(raw, emails);
    if (nextEmails.length > emails.length) {
      setEmails(nextEmails);
    }
    setInputValue(leftover);
    setHasError(Boolean(leftover));
    return leftover;
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (!SEPARATORS_REGEX.test(value)) {
      setHasError(false);
    }
  };

  useEffect(() => {
    if (SEPARATORS_REGEX.test(inputValue)) {
      commitEmails(inputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();
      event.stopPropagation();
      commitEmails(inputValue);
    }
  };

  const handleBlur = () => {
    const leftover = commitEmails(inputValue);
    void logUserInputEvent({
      inputName: "notificationEmails",
      isEmpty: emails.length === 0 && !leftover,
      isValid: !leftover,
    })();
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((existing) => existing !== email));
  };

  const startEditingEmail = (email: string) => {
    const { nextEmails } = splitEmails(inputValue, emails);
    setEmails(nextEmails.filter((existing) => existing !== email));
    setInputValue(email);
    setHasError(false);
    inputRef.current?.focus();
  };

  return (
    <Box onKeyDownCapture={handleKeyDownCapture}>
      <DashboardAutocompleteV2<string, true, true, true>
        multiple
        freeSolo
        disableClearable
        options={[]}
        value={emails}
        onChange={(newValue) => {
          const nextEmails = ((newValue ?? []) as string[]).filter((entry) => isEmail(entry));
          setEmails(nextEmails);
        }}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          if (reason === "reset") {
            return;
          }
          handleInputChange(value);
        }}
        onBlur={handleBlur}
        inputRef={inputRef}
        label={label}
        placeholder={t("dashboard", "step.advancedSettings.notificationEmails.placeholder")}
        errorMessage={hasError ? t("dashboard", "step.advancedSettings.notificationEmails.invalidEmail") : undefined}
        hideDropdownIcon
        renderTags={(tags) => (
          <>
            {(tags as string[]).map((email) => (
              <DashboardChip
                key={email}
                label={email}
                variant="filled"
                vibe="neutral"
                shape="rounded"
                onClick={() => startEditingEmail(email)}
                onDelete={(event) => {
                  event.stopPropagation();
                  removeEmail(email);
                }}
                data-sensitive="email"
              />
            ))}
          </>
        )}
        data-test="advanced-settings-notification-emails-input"
        sx={{
          "& .MuiInputBase-root": {
            flexWrap: "wrap !important",
            height: "auto",
            gap: 0.5,
          },
          "& .MuiInputBase-input": {
            flexGrow: 1,
            minWidth: "80px",
            caretColor: "auto !important",
          },
        }}
      />
    </Box>
  );
};
